import React, { useEffect, useRef, useState } from "react";

const scrollData = {
  en: {
    header: "Explore Our Solutions",
    items: [
      {
        title: "Enterprise AI Management",
        points: [
          "AI-driven unified platform integrating ERP, OA, CRM systems",
          "Smart workflow engine with automated approval & task assignment, 200% efficiency boost",
          "Real-time data dashboard for management to monitor operations anytime",
          "Modular architecture supporting rapid expansion and business iteration",
        ],
      },
      {
        title: "AI Customer Service System",
        points: [
          "Large model semantic understanding, 95%+ accuracy in intent recognition",
          "Multi-channel integration (WeChat/Website/App) with unified agent workspace",
          "Smart ticket routing, complex issues auto-escalated to human agents",
          "Auto-built customer profiles for precision marketing and conversion boost",
        ],
      },
      {
        title: "Local AI Deployment",
        points: [
          "Private LLM deployment, data stays in-house for compliance",
          "Support mainstream open-source models (Llama/Qwen/Baichuan)",
          "GPU resource optimization, 60% inference cost reduction",
          "Enterprise knowledge base RAG for business-aware AI responses",
        ],
      },
      {
        title: "AI Document & Invoice Processing",
        points: [
          "OCR recognition for invoices, contracts, receipts with 99%+ accuracy",
          "Auto classification and archiving, one-click voucher generation",
          "Smart anomaly detection to avoid tax compliance risks",
          "Integration with major accounting software for seamless data sync",
        ],
      },
      {
        title: "AI + GIS",
        points: [
          "Satellite remote sensing + drone inspection for 24/7 area monitoring",
          "AI image recognition for vegetation changes, water pollution, illegal construction",
          "Disaster prediction model, 2-4 hour early warning for wildfires/floods",
          "Spatiotemporal data visualization for urban planning and emergency response",
        ],
      },
    ],
  },
  zh: {
    header: "了解我们能提供哪些解决方案",
    items: [
      {
        title: "企业智能管理系统",
        points: [
          "AI 驱动的统一管理平台，整合 ERP、OA、CRM 多系统数据",
          "智能工作流引擎，自动化审批与任务分配，效率提升 200%",
          "实时数据看板，管理层随时掌握企业运营状态",
          "模块化架构，支持企业快速扩张与业务迭代",
        ],
      },
      {
        title: "智能客服系统",
        points: [
          "大模型语义理解，准确识别客户意图，回答准确率 95%+",
          "多渠道接入（微信/网站/APP），统一客服工作台管理",
          "智能工单流转，复杂问题自动升级人工处理",
          "客户画像自动构建，精准营销推荐提升转化率",
        ],
      },
      {
        title: "本地 AI 部署",
        points: [
          "私有化大模型部署，数据不出内网，满足合规要求",
          "支持主流开源模型（Llama/Qwen/Baichuan）快速接入",
          "GPU 资源优化调度，推理成本降低 60%",
          "企业知识库 RAG 检索，AI 回答更懂你的业务",
        ],
      },
      {
        title: "AI 文档与发票处理",
        points: [
          "OCR 智能识别各类发票、合同、单据，识别率 99%+",
          "自动分类归档，财务凭证一键生成",
          "异常票据智能检测，规避税务风险",
          "对接主流财务软件，数据无缝同步",
        ],
      },
      {
        title: "AI + 地理信息系统",
        points: [
          "卫星遥感 + 无人机巡检，大范围区域全天候监测",
          "AI 图像识别，自动检测植被变化、水体污染、违章建筑",
          "灾害预警模型，山火/洪水提前 2-4 小时预警",
          "时空数据可视化，辅助城市规划与应急决策",
        ],
      },
    ],
  },
  es: {
    header: "Explora Nuestras Soluciones",
    items: [
      {
        title: "Gestión Empresarial con IA",
        points: [
          "Plataforma unificada impulsada por IA integrando ERP, OA, CRM",
          "Motor de flujo de trabajo inteligente, aprobación automatizada, 200% más eficiencia",
          "Panel de datos en tiempo real para supervisar operaciones",
          "Arquitectura modular que soporta expansión rápida e iteración de negocio",
        ],
      },
      {
        title: "Sistema de Atención al Cliente con IA",
        points: [
          "Comprensión semántica con modelo grande, 95%+ precisión en intención",
          "Integración multicanal (WeChat/Web/App) con espacio de trabajo unificado",
          "Enrutamiento inteligente de tickets, escalado automático a agentes humanos",
          "Perfiles de cliente automáticos para marketing preciso y mayor conversión",
        ],
      },
      {
        title: "Implementación Local de IA",
        points: [
          "Despliegue privado de LLM, datos dentro de la red para cumplimiento",
          "Soporte para modelos open-source principales (Llama/Qwen/Baichuan)",
          "Optimización de recursos GPU, 60% reducción de costos de inferencia",
          "Base de conocimiento empresarial RAG para respuestas de IA contextualizadas",
        ],
      },
      {
        title: "Procesamiento de Documentos y Facturas con IA",
        points: [
          "Reconocimiento OCR de facturas, contratos, recibos con 99%+ precisión",
          "Clasificación y archivado automático, generación de comprobantes con un clic",
          "Detección inteligente de anomalías para evitar riesgos fiscales",
          "Integración con software contable principal para sincronización de datos",
        ],
      },
      {
        title: "IA + GIS",
        points: [
          "Teledetección satelital + inspección con drones para monitoreo 24/7",
          "Reconocimiento de imágenes IA para cambios de vegetación, contaminación, construcciones ilegales",
          "Modelo de predicción de desastres, alerta temprana 2-4 horas para incendios/inundaciones",
          "Visualización de datos espaciotemporales para planificación urbana y respuesta a emergencias",
        ],
      },
    ],
  },
};

export { scrollData };

export default function ScrollFeature({ title, img, points, isHeader, lang = "zh", index }) {
  const ref = useRef();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShow(true);
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => observer.disconnect();
  }, []);

  if (isHeader) {
    const t = scrollData[lang];
    return (
      <div
        ref={ref}
        className={`scroll-feature-header ${show ? 'visible' : ''}`}
      >
        <h2 className="scroll-feature-header-title">
          {t.header}
        </h2>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={`scroll-feature ${show ? 'visible' : ''}`}
    >
      <img 
        src={img} 
        alt={title}
        className="scroll-feature-image"
        loading="lazy"
      />

      <div className="scroll-feature-content">
        <h2 className="scroll-feature-title">
          {title}
        </h2>
        
        {points && points.length > 0 && (
          <ul className="scroll-feature-points">
            {points.map((point, index) => (
              <li key={index} className="scroll-feature-point">
                {point}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
